import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Mic, LayoutDashboard, TrendingUp, MessageSquare, Camera, Check, Sparkles } from "lucide-react";

const features = [
  {
    id: "ai-recording",
    tag: "IA POTENCIADA",
    tagIcon: Sparkles,
    icon: Mic,
    title: "Grabación Inteligente de Consultas",
    description: "La IA escucha y genera automáticamente todo lo que necesitas.",
    highlights: [
      "Resumen ejecutivo de la consulta",
      "Puntos clave y observaciones",
      "Modificaciones sugeridas al plan",
      "Acciones a seguir para coach y cliente",
      "Detección de estado de ánimo",
    ],
    isMain: true,
    colSpan: "md:col-span-7",
  },
  {
    id: "dashboard",
    tag: "CORE",
    icon: LayoutDashboard,
    title: "Dashboard de Mediciones",
    description: "10 perímetros y 8 pliegues con comparativas automáticas. Vista tabla en escritorio, cards en móvil.",
    colSpan: "md:col-span-5",
  },
  {
    id: "graphs",
    tag: "CORE",
    icon: TrendingUp,
    title: "Gráficas de Evolución",
    description: "7 gráficas interactivas: peso, % grasa, radar de perímetros, pliegues individuales, suma, heatmap corporal.",
    colSpan: "md:col-span-4",
  },
  {
    id: "feedback",
    tag: "CORE",
    icon: MessageSquare,
    title: "Coach Feedback por Medición",
    description: "Genera notas profesionales por check-in: composición corporal, recomendaciones, alertas y metas.",
    colSpan: "md:col-span-4",
  },
  {
    id: "photos",
    tag: "CORE",
    icon: Camera,
    title: "Fotos Comparativas",
    description: "Sube fotos por medición y compara lado a lado. El progreso visual es el mayor motivador.",
    colSpan: "md:col-span-4",
  },
];

export const FeaturesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="funcionalidades"
      ref={ref}
      className="relative py-24 md:py-32 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-block text-[#E05D3A] text-sm font-semibold tracking-widest uppercase mb-4"
          >
            Funcionalidades
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-6"
          >
            Todo lo que necesitas. <span className="text-gradient">Nada que no.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-zinc-400 text-base sm:text-lg max-w-2xl mx-auto"
          >
            Cada función está diseñada alrededor de un problema real que vive un 
            nutricionista en cada consulta.
          </motion.p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
              className={`group relative rounded-2xl border transition-all duration-300 ${feature.colSpan} ${
                feature.isMain
                  ? "bg-gradient-to-br from-[#E05D3A]/10 to-transparent border-[#E05D3A]/30 hover:border-[#E05D3A]/50"
                  : "bg-white/[0.02] border-white/10 hover:bg-white/[0.04] hover:border-white/20"
              }`}
              data-testid={`feature-card-${feature.id}`}
            >
              <div className="p-6 md:p-8 h-full flex flex-col">
                {/* Tag */}
                <div className="flex items-center gap-2 mb-4">
                  {feature.tagIcon && (
                    <feature.tagIcon className="w-4 h-4 text-[#E05D3A]" />
                  )}
                  <span className={`text-xs font-semibold tracking-wider ${
                    feature.isMain ? "text-[#E05D3A]" : "text-zinc-500"
                  }`}>
                    {feature.tag}
                  </span>
                </div>

                {/* Icon */}
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${
                  feature.isMain ? "bg-[#E05D3A]/20 text-[#E05D3A]" : "bg-white/5 text-zinc-400"
                } group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-6 h-6" />
                </div>

                {/* Content */}
                <h3 className="font-outfit text-lg md:text-xl font-medium text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-4">
                  {feature.description}
                </p>

                {/* Highlights for main feature */}
                {feature.highlights && (
                  <ul className="mt-auto space-y-2">
                    {feature.highlights.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                        <Check className="w-4 h-4 text-[#E05D3A] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
